import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Link,
    useColorModeValue,
    Divider,
    List,
    ListItem,
    ListIcon,
    Icon,
} from '@chakra-ui/react';
import { FiCheck, FiArrowLeft } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

function CaptainTermsAndConditions() {
    const textColor = useColorModeValue('gray.700', 'white');
    const bgColor = useColorModeValue('white', 'gray.800');
    const brandColor = useColorModeValue('brand.500', 'brand.400');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    return (
        <Box minH="100vh" bg={bgColor}>
            {/* Header */}
            <Box
                bgImage="linear-gradient(to right, rgba(255, 26, 61, 0.85), rgba(255, 94, 0, 0.85)), url('/assets/img/food.jpg')"
                bgSize="cover"
                bgPosition="center"
                height="30vh"
                position="relative"
                boxShadow="xl"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <VStack spacing={4}>
                    <Heading color="white" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
                        Magauu Captain Terms and Conditions
                    </Heading>
                    <Text color="white" fontSize={{ base: "md", md: "lg" }} textAlign="center" maxW="md">
                        Please read these terms carefully before using our platform
                    </Text>
                </VStack>
            </Box>

            {/* Back Button */}
            <Container maxW="container.md" py={6}>
                <Link as={RouterLink} to="/sign-in" color={brandColor} display="inline-flex" alignItems="center" mb={6}>
                    <Icon as={FiArrowLeft as any} style={{ marginRight: '8px' }} color={brandColor} />
                    Back to Sign In
                </Link>
            </Container>

            {/* Content */}
            <Container maxW="container.md" py={8}>
                <VStack spacing={8} align="stretch">
                    <Box>
                        <Text color="gray.500" fontSize="sm" mb={4}>
                            Last updated: {new Date().toLocaleDateString()}
                        </Text>

                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            Welcome to the Magauu Captain Platform. These Terms and Conditions govern your use of our service as a food delivery captain and constitute a legally binding agreement between you and our company.
                        </Text>
                    </Box>

                    <Divider borderColor={borderColor} />

                    {/* Acceptance of Terms */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            1. Acceptance of Terms
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            By accessing and using this platform, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                        </Text>
                    </Box>

                    {/* Description of Service */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            2. Description of Service
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            Our platform provides food delivery services to captains including but not limited to:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Receiving and accepting delivery orders</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Navigation and route optimization</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Tracking earnings and payouts</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Customer communication tools</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Performance analytics</Text>
                            </ListItem>
                        </List>
                    </Box>

                    {/* User Accounts */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            3. Captain Accounts and Registration
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            To access certain features of our service, you must register for a Captain account. You agree to:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Provide accurate and complete personal and vehicle information</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Maintain the security of your account credentials</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Notify us immediately of any unauthorized use</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Accept responsibility for all activities under your account</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Comply with all local traffic and safety laws</Text>
                            </ListItem>
                        </List>
                    </Box>

                    {/* Acceptable Use */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            4. Acceptable Use Policy
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            You agree not to use the service to:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="red.500" />
                                <Text color={textColor} fontSize="md">Violate any applicable laws or regulations (e.g., traffic laws)</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="red.500" />
                                <Text color={textColor} fontSize="md">Infringe upon intellectual property rights</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="red.500" />
                                <Text color={textColor} fontSize="md">Transmit harmful, offensive, or inappropriate content</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="red.500" />
                                <Text color={textColor} fontSize="md">Attempt to gain unauthorized access to our systems</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="red.500" />
                                <Text color={textColor} fontSize="md">Interfere with the proper functioning of the service, including timely deliveries</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="red.500" />
                                <Text color={textColor} fontSize="md">Misrepresent your identity or vehicle information</Text>
                            </ListItem>
                        </List>
                    </Box>

                    {/* Privacy and Data */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            5. Privacy and Data Protection
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our service, you consent to the collection and use of information as outlined in our Privacy Policy.
                        </Text>
                    </Box>

                    {/* Payment Terms */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            6. Payment Terms
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            For services rendered as a Captain, the following terms apply:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Earnings will be calculated based on completed deliveries and applicable bonuses</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Payouts will be processed according to the agreed-upon schedule (e.g., weekly)</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">We reserve the right to adjust payment terms with reasonable notice</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Any discrepancies in payments should be reported within 7 days</Text>
                            </ListItem>
                        </List>
                    </Box>

                    {/* Intellectual Property */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            7. Intellectual Property Rights
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            The service and its original content, features, and functionality are owned by us and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You retain ownership of content you submit, but grant us a license to use it for service provision.
                        </Text>
                    </Box>

                    {/* Limitation of Liability */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            8. Limitation of Liability
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            In no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
                        </Text>
                    </Box>

                    {/* Termination */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            9. Termination
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            We may terminate or suspend your account and bar access to the service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever, including without limitation if you breach the Terms.
                        </Text>
                    </Box>

                    {/* Changes to Terms */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            10. Changes to Terms
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                        </Text>
                    </Box>

                    {/* Contact Information */}
                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            11. Contact Information
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            If you have any questions about these Terms and Conditions, please contact us at:
                        </Text>
                        <VStack align="start" mt={4} spacing={2}>
                            <Text color={textColor} fontSize="md">
                                Email: magauuapp@gmail.com
                            </Text>
                            <Text color={textColor} fontSize="md">
                                Phone: +9779860363646
                            </Text>
                            {/* <Text color={textColor} fontSize="md">
                                Address: 123 Business Street, Suite 100, City, State 12345
                            </Text> */}
                        </VStack>
                    </Box>

                    <Divider borderColor={borderColor} />

                    <Box textAlign="center">
                        <Text color="gray.500" fontSize="sm">
                            By using our service, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}

export default CaptainTermsAndConditions;
